import { marquesMontrees } from '@/lib/ajouts-texte';
import type { AnalyseTexte, ControleNoyau } from '@rosetta/shared';
import { BandeauFinalite } from './bandeau-finalite';
import { ColonneTexte } from './colonne-texte';
import { LegendeMarques } from './legende-marques';
import { ListeInvariants } from './liste-invariants';

interface AvantApresProps {
  /** The text the user typed or loaded. */
  avant: string;
  /** The translated text of the noyau. */
  apres: string;
  /**
   * What Tahiti says about the text (#9). Without it (not read yet, unreadable, or the call
   * failed) the panel is what it was: the two texts, as they are.
   */
  analyse?: AnalyseTexte | undefined;
  /**
   * The quality control of the noyau shown (#23): its additions are marked in the translated text.
   * Without it (none, unreadable, still loading) the panel is what it was.
   */
  controle?: ControleNoyau | undefined;
}

/**
 * Panel of the "Avant / après" tab: the purpose of the text (when the analysis gives one),
 * the key of the marks (#30), the two texts side by side with their invariants marked, then the
 * list of the invariants. The additions of the control (#23) and the changes the translation
 * declares (#28) are marked in the translated text, their keys are in the legend with the others.
 */
export function AvantApres({ avant, apres, analyse, controle }: AvantApresProps) {
  const invariants = analyse?.invariants ?? null;
  const ajouts = controle?.ajouts ?? [];
  // The changes the translation declares (#28).
  const changements = controle?.changements ?? [];
  // Only the marks written in the text as Tahiti quotes them are shown (and keyed).
  const montrees = marquesMontrees(apres, ajouts, changements);
  return (
    <div className="flex flex-col gap-6">
      {analyse?.finalite == null ? null : <BandeauFinalite finalite={analyse.finalite} />}
      <LegendeMarques
        avecInvariants={invariants !== null && invariants.length > 0}
        avecAjouts={montrees.ajouts}
        avecChangements={montrees.changements}
      />
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-12">
        <ColonneTexte titre="Texte d'origine" texte={avant} invariants={invariants} />
        <ColonneTexte
          titre="Texte traduit"
          texte={apres}
          invariants={invariants}
          ajouts={ajouts}
          changements={changements}
        />
      </div>
      {invariants === null ? null : <ListeInvariants invariants={invariants} />}
    </div>
  );
}
